import PropTypes from 'prop-types';
import React, { Fragment } from 'react';
import { connect } from 'react-redux';
import { withState, withProps, setStatic, withHandlers, compose } from 'recompose';
import { SegmentedButton, Badge } from '@ontruck/orc';
import { getTotalByNoOwner } from 'redux/modules/journeys/selectors';
import { injectIntl } from 'react-intl';
import AssignAgentSelector from 'modules/journey/AssignAgentSelector';

import { handledByFilter } from './constants';
import { StyledFilter } from './styled';

export const getSegmentedValue = (value, meId) => {
  if (!value) {
    return handledByFilter.all;
  }

  if (value === handledByFilter.all) {
    return handledByFilter.all;
  }

  if (value === handledByFilter.no_owner) {
    return handledByFilter.no_owner;
  }

  if (typeof value === 'object') {
    if (value.id === meId) {
      return handledByFilter.mine;
    }

    return handledByFilter.select;
  }
};

export const values = {
  [handledByFilter.all]: () => handledByFilter.all,
  [handledByFilter.no_owner]: () => handledByFilter.no_owner,
  [handledByFilter.mine]: ({ me }) => ({
    id: me.id,
    email: me.email,
    label: me.name,
  }),
};

export class RefsStore {
  store(name, value) {
    this[name] = value;
  }
}

const refsStore = new RefsStore();

const enhance = compose(
  connect((state, { showTotalNoOwner = true, filters }) => ({
    totalNoOwner: showTotalNoOwner && getTotalByNoOwner(state),
    me: state.me || {},
  })),
  setStatic('filterName', 'HandledByFilter'),
  withState('selectAgentOpened', 'toggleSelectAgentOpened', false),
  withProps(({ value, me }) => ({
    segmentedValue: getSegmentedValue(value, me.id),
    refs: refsStore,
  })),
  withHandlers(({ refs }) => ({
    handleChange:
      ({ onChange, me, toggleSelectAgentOpened }) =>
      (_, { value }) => {
        if (!onChange) {
          return;
        }

        if (value === handledByFilter.select) {
          toggleSelectAgentOpened(true);
          if (refs && refs.selectRef && refs.selectRef.select) {
            setTimeout(() => {
              refs.selectRef.select.input.input.focus();
            }, 100);
          }
          return;
        }

        const realValue = values[value]({ me });

        onChange(realValue);
      },
    handleSelectAgent:
      ({ onChange, toggleSelectAgentOpened }) =>
      ({ agent }) => {
        if (!onChange) {
          return;
        }

        if (!agent) {
          onChange(handledByFilter.no_owner);
          toggleSelectAgentOpened(false);
          return;
        }

        const { id, label, value } = agent;
        onChange({
          id,
          label,
          value,
        });
      },
    onSelectMounted:
      ({ refs }) =>
      (ref) => {
        if (!ref) {
          return;
        }
        refs.store('selectRef', ref);
      },
    handleBlur:
      ({ toggleSelectAgentOpened, value }) =>
      () => {
        if (!value || typeof value === 'string') {
          toggleSelectAgentOpened(false);
        }
      },
  }))
);

const HandledByFilter = ({
  intl,
  value,
  selectAgentOpened,
  segmentedValue,
  handleChange,
  handleSelectAgent,
  stylingProps,
  totalNoOwner,
  onSelectMounted,
  handleBlur,
  filterUserGroups,
  me,
  minWidth = 350,
}) => {
  const isSelectOptionSelected =
    selectAgentOpened ||
    (value !== null && typeof value === 'object' && value.id !== (me || {}).id);
  const t = intl.messages.modules.Filters.handled_by_filter;
  return (
    <StyledFilter {...stylingProps}>
      {!isSelectOptionSelected && (
        <SegmentedButton.Parent
          size="M"
          value={segmentedValue}
          onChange={handleChange}
          dataE2E="filters__handledBy"
        >
          <SegmentedButton.Child value={handledByFilter.all} label={t.all} />
          <SegmentedButton.Child
            value={handledByFilter.mine}
            label={<Fragment>{t.mine}</Fragment>}
            dataE2E="filters__handledBy_mine"
          />
          <SegmentedButton.Child
            value={handledByFilter.no_owner}
            label={
              <Fragment>
                {t.no_owner}&nbsp;
                {!!totalNoOwner && <Badge text={totalNoOwner} size="S" skin="primary" />}
              </Fragment>
            }
            dataE2E="filters__handledBy_noOwner"
          />
          <SegmentedButton.Child
            value={handledByFilter.select}
            label={<Fragment>{t.select}</Fragment>}
            dataE2E="filters__handledBy_select"
          />
        </SegmentedButton.Parent>
      )}
      <div style={{ display: isSelectOptionSelected ? 'block' : 'none' }}>
        <AssignAgentSelector
          value={value}
          onChange={handleSelectAgent}
          minWidth={minWidth}
          selectRef={onSelectMounted}
          onBlur={handleBlur}
          autoFocus={false}
          filterUserGroups={filterUserGroups}
        />
      </div>
    </StyledFilter>
  );
};

HandledByFilter.propTypes = {
  value: PropTypes.any,
  handleChange: PropTypes.func.isRequired,
  stylingProps: PropTypes.object.isRequired,
  totals: PropTypes.object,
  totalNoOwner: PropTypes.number,
  selectAgentOpened: PropTypes.bool,
  segmentedValue: PropTypes.string,
  handleSelectAgent: PropTypes.func,
  onSelectMounted: PropTypes.func,
  handleBlur: PropTypes.func,
  me: PropTypes.object,
};

HandledByFilter.defaultProps = {
  value: handledByFilter.all,
  totals: {},
};
export default injectIntl(enhance(HandledByFilter));
